import React, {useEffect, useState} from 'react'
import {Field, FieldsManager} from '../../../data/services/fields'
import LocalStorage from '../../../util/localStorage'
import {useDispatch, useSelector} from 'react-redux'
import Resources from '../../../data/services/resources'
import {getSecondResource} from '../../../data/actions/secondResource'
import PayDateFilter from '../../accounting/carrier-pay/carrier-pay-date-filter'
import {
    classNames,
    getDefaultTableOptions,
    getProp,
    saveTableColumns
} from '../../../common/util/util-helpers'
import {download} from '../../../data/actions/download'
import {currentDate} from '../../../common/util/util-dates'
import DocumentArrowDownIcon from '@heroicons/react/20/solid/DocumentArrowDownIcon'
import Tippy from '@tippyjs/react'
import {CubeIcon} from "@heroicons/react/24/outline"
import TableCard from "../../../common/components/resource-table/table-components/table-card"
import NoRecordsTable from "../../../common/components/no-records-found/no-records-table"
import TableFilters from "../../../common/components/resource-table/table-components/table-filters"
import ActiveFilters from "../../../common/components/resource-table/table-components/active-filters"
import Badge from "../../../common/components/badge"
import ResourceTable from "../../../common/components/resource-table"
import {excludeFields} from "../../../common/util/util-fields"
import moment from "moment"
import {LoaderSmall} from "../../../common/components/loader"
import {numberWithCommas} from "../../../util/util-formaters"
import TableCardFooter from "../../../common/components/resource-table/table-components/table-card-footer";
import Pagination from "../../../common/components/resource-table/table-components/pagination";

export default function StockMovementReportTab({
                                                   translate,
                                                   pagePath
                                               }) {

    /** Store
     ================================================================= */
    const dispatch = useDispatch()
    const secondResource = useSelector((state) => state.secondResource)
    const isLoading = secondResource.isLoading
    const data = secondResource?.data?.list ?? []
    const count = getProp(secondResource.data, 'count', 0)

    /** Helpers
     ================================================================= */

    const getFields = () => {
        return {
            SKU: new Field('SKU', '', [''], false, 'text', {}),
            ProductService: new Field('ProductService', '', [''], false, 'text', {}),
            OpeningStock: new Field('OpeningStock', '', [''], false, 'decimal', {
                render: (item) => numberWithCommas(item.OpeningStock ?? 0, 2)
            }),
            PurchasedInPeriod: new Field('PurchasedInPeriod', '', [''], false, 'decimal', {
                render: (item) => numberWithCommas(item.PurchasedInPeriod ?? 0, 2)
            }),
            CreditedInPeriod: new Field('CreditedInPeriod', '', [''], false, 'decimal', {
                render: (item) => numberWithCommas(item.CreditedInPeriod ?? 0, 2)
            }),
            UsedInPeriod: new Field('UsedInPeriod', '', [''], false, 'decimal', {
                render: (item) => numberWithCommas(item.UsedInPeriod ?? 0, 2)
            }),
            ClosingStock: new Field('ClosingStock', '', [''], false, 'decimal', {
                render: (item) => numberWithCommas(item.ClosingStock ?? 0, 2)
            }),
            AvgPurchasePrice: new Field('AvgPurchasePrice', '', [''], false, 'decimal', {
                render: (item) => item.AvgPurchasePrice ? '$' + numberWithCommas(item.AvgPurchasePrice, 2) : '-'
            }),
            AvgSellPrice: new Field('AvgSellPrice', '', [''], false, 'decimal', {
                render: (item) => item.AvgSellPrice ? '$' + numberWithCommas(item.AvgSellPrice, 2) : '-'
            }),
        }
    }

    const getQueryFields = () => {
        return {
            query: new Field('query', '', [''], false, 'search', {hideLabel: true, labelType: "float"}),
            StartDate: new Field('StartDate', moment().startOf('year').format('YYYY-MM-DD'), [], false, 'date', {}, {}),
            EndDate: new Field('EndDate', moment().format('YYYY-MM-DD'), [], false, 'date', {}, {}),
            offset: new Field('offset', '0', [''], false, 'text'),
            limit: new Field('limit', '100', [''], false, 'select', {hideLabel: true, labelType: "float"}, {
                menuPlacement: 'top',
                hasPortal: true
            })
        }
    }

    const getResourceName = () => {
        return Resources.StockTracking
    }

    const getQuery = () => {
        return {
            ...FieldsManager.getFieldKeyValues(queryFilterFields),
            order: sort,
            orderBy: sortBy
        }
    }

    const getTableOptions = (pagePath, translate) => {
        const tablePageDefaults = {
            behaviour: {
                rowSelect: false,
                canAdjustWidth: true,
                hasMenu: false
            },
            style: {
                condensed: true,
                isGPUAccelerated: true
            }
        }

        return getDefaultTableOptions(getFields(), tablePageDefaults, pagePath, translate)
    }

    /** State
     ================================================================= */
    const [queryFilterFields, setQueryFilterFields] = useState(getQueryFields())
    const [tableOptions, setTableOptions] = useState(getTableOptions(pagePath, translate))
    const [isTableFullScreen, setIsTableFullScreen] = useState(false)
    const [sort, setSort] = useState("ASC")
    const [sortBy, setSortBy] = useState("SKU")

    const metadata = {}

    /** Data events
     ================================================================= */
    const fetchData = () => {
        dispatch(getSecondResource({
            user: LocalStorage.get('user'),
            resource: getResourceName(),
            query: getQuery()
        }))
    }

    const downloadExcel = () => {
        dispatch(download({
            user: LocalStorage.get('user'),
            resource: getResourceName(),
            query: Object.assign({
                format: 'EXCEL',
                name: 'stock_movement_report_' + currentDate() + '.xlsx'
            }, getQuery())
        }))
    }

    /** Lifecycle
     ================================================================= */
    useEffect(() => {
        fetchData()
    }, [sort, sortBy, queryFilterFields])

    /** UI events
     ================================================================= */
    const handleFilterInputChange = (name, value) => {
        let queryFieldsUpdate = FieldsManager.updateField(queryFilterFields, name, value)

        if (name === 'StartDate') {
            queryFieldsUpdate.EndDate.props.minDate = value
        }
        if (name === 'EndDate') {
            queryFieldsUpdate.StartDate.props.maxDate = value
        }

        setQueryFilterFields(queryFieldsUpdate)
    }

    const handleClearFiltersClick = (excludeAdditional = []) => {
        const queryFilterFieldsTmp = Object.assign({}, queryFilterFields)
        const defaultExcludedFields = ['limit']
        const excludedFields = defaultExcludedFields.concat(excludeAdditional)

        Object.values(queryFilterFieldsTmp).filter((it) => !excludedFields.includes(it.name)).forEach((it) => {
            FieldsManager.updateField(queryFilterFieldsTmp, it.name, '')
        })

        setQueryFilterFields(queryFilterFieldsTmp)
    }

    const handleFullScreenToggle = () => {
        setIsTableFullScreen(!isTableFullScreen)
    }

    const setOptions = (options) => {
        setTableOptions(options)
        saveTableColumns(pagePath, options)
    }

    const handleUpdateSort = (sortByTmp) => {
        setSort((sortByTmp === sortBy) ? (sort === "ASC" ? "DESC" : "ASC") : "ASC")
        setSortBy(sortByTmp)
    }

    /** Render
     ================================================================= */
    return (
        <React.Fragment>
            <div className={"flex items-center mb-4"}>
                <div className="flex mx-4 gap-4">
                    <Badge addClass={"relative"} type="outline">
                        {isLoading && (
                            <div className="px-5">
                                <LoaderSmall/>
                            </div>
                        )}

                        {!isLoading && (
                            <div className="flex items-center gap-1.5">
                                <CubeIcon className="w-5 h-5"/>
                                <span>Products:</span>
                                <span className="font-bold">{count}</span>
                            </div>
                        )}
                    </Badge>
                </div>
            </div>

            <div className={"sm:flex justify-between"}>
                <ActiveFilters
                    addClass={"mb-2"}
                    filterFields={queryFilterFields}
                    onLabelClick={handleFilterInputChange}
                    persistFilters={[]}
                    onClearFiltersClick={handleClearFiltersClick}
                    translate={translate}
                />

                <Tippy content={translate('text.download_excel')}>
                    <button
                        className="btn-icon"
                        onClick={downloadExcel}
                    >
                        <DocumentArrowDownIcon className="w-6 h-6"/>
                    </button>
                </Tippy>
            </div>

            <TableCard
                className={
                    classNames(
                        "",
                        isTableFullScreen
                            ? "bg-inverse rounded-card fixed inset-1 -top-1 z-50"
                            : "flex flex-col h-dialog-body md:bg-inverse md:border border-tm-gray-300 md:shadow-card"
                    )}
            >
                <div>
                    <TableFilters
                        forceDialog
                        hideLimit
                        filterFields={excludeFields(queryFilterFields, ['StartDate', 'EndDate'])}
                        handleInputChange={handleFilterInputChange}
                        translate={translate}
                        onRefreshTable={fetchData}
                        selects={metadata}
                        onFullScreenToggle={handleFullScreenToggle}
                        isTableFullScreen={isTableFullScreen}
                        isLoading={isLoading}
                        customHtml={
                            <PayDateFilter
                                queryFields={queryFilterFields}
                                translate={translate}
                                hasDateType={false}
                                updateQueryFields={(queryFieldsUpdate) => {
                                    setQueryFilterFields(queryFieldsUpdate)
                                }}
                                onQueryChange={handleFilterInputChange}
                            />
                        }
                    />
                </div>

                <ResourceTable
                    tableKey={'ProductServiceID'}
                    data={data}
                    count={count}
                    fields={getFields()}
                    options={tableOptions}
                    isLoading={isLoading}
                    translate={translate}
                    sort={sort}
                    sortBy={sortBy}
                    onSortChange={handleUpdateSort}
                    saveTableOptions={setOptions}
                    maxHeightClass={"max-h-[calc(100vh-28rem)]"}
                />

                <NoRecordsTable
                    show={(data.length === 0) && !isLoading}
                    title={'No matching records found'}
                    className={"pb-12 pt-16 px-6"}
                />

                <TableCardFooter
                    show={!(!data.length && !isLoading)}
                >
                    <Pagination
                        count={count}
                        isLoading={isLoading}
                        handleQueryChange={handleFilterInputChange}
                        queryFields={queryFilterFields}
                        translate={translate}
                    />
                </TableCardFooter>
            </TableCard>
        </React.Fragment>
    )
}